import { useCallback, useEffect, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { TaskList } from '@/types/task-lists';

const STORAGE_KEY = 'todo.activeListId';

type UseActiveTaskList = {
  activeListId: string | null;
  setActiveListId: (id: string) => void;
};

/**
 * Persists the last-selected to-do list id. Falls back to the default list
 * if the persisted id is missing from the fetched collection.
 */
export function useActiveTaskList(
  lists: TaskList[],
  defaultListId: string | null,
): UseActiveTaskList {
  const [activeListId, setActiveListIdState] = useState<string | null>(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from AsyncStorage once.
  useEffect(() => {
    let mounted = true;
    AsyncStorage.getItem(STORAGE_KEY).then(stored => {
      if (mounted) {
        setActiveListIdState(stored);
        setHydrated(true);
      }
    });
    return () => { mounted = false; };
  }, []);

  // Resolve to a valid list whenever `lists` updates.
  useEffect(() => {
    if (!hydrated || lists.length === 0) return;
    const isValid = activeListId && lists.some(l => l.id === activeListId);
    if (!isValid && defaultListId) {
      setActiveListIdState(defaultListId);
    }
  }, [hydrated, lists, activeListId, defaultListId]);

  const setActiveListId = useCallback((id: string) => {
    setActiveListIdState(id);
    AsyncStorage.setItem(STORAGE_KEY, id).catch(error => {
      console.warn('[useActiveTaskList] persist failed:', error);
    });
  }, []);

  return { activeListId, setActiveListId };
}
